import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { statsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  BarChart3,
  Lock,
  Unlock,
  Coins,
  Target,
  Users,
  Trophy,
  TrendingUp,
  Shield,
  Percent,
  Zap,
} from 'lucide-react';
import ClubRosterDialog from '../components/ClubRosterDialog';
import { io } from 'socket.io-client';

const Stats = () => {
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const isItalian = i18n.language === 'it';

  const [statsStatus, setStatsStatus] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rosterClub, setRosterClub] = useState(null);
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    checkStatsStatus();
  }, []);

  const checkStatsStatus = async () => {
    try {
      const res = await statsApi.getStatus();
      setStatsStatus(res.data);

      if (res.data.stats_unlocked) {
        fetchAdvancedStats();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking stats status:', error);
      setLoading(false);
    }
  };

  const fetchAdvancedStats = async () => {
    try {
      const res = await statsApi.getAdvanced();
      setAdvancedStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error(isItalian ? 'Errore nel caricamento' : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  // Live update: refresh when standings change
  useEffect(() => {
    if (!statsStatus?.stats_unlocked) return;
    const BACKEND_URL = window.location.origin;
    const socket = io(BACKEND_URL, { transports: ['websocket', 'polling'], reconnectionAttempts: 3 });
    socket.on('standings_updated', () => { fetchAdvancedStats(); });
    const interval = setInterval(fetchAdvancedStats, 30000);
    return () => { clearInterval(interval); socket.disconnect(); };
  }, [statsStatus?.stats_unlocked]);

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      const res = await statsApi.unlock();
      toast.success(res.data.message);
      setStatsStatus({ ...statsStatus, stats_unlocked: true });
      if (refreshUser) refreshUser();
      fetchAdvancedStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore nello sblocco' : 'Failed to unlock'));
    } finally {
      setUnlocking(false);
    }
  };

  const getFormBadge = (result) => {
    switch (result) {
      case 'W':
        return <span className="w-6 h-6 flex items-center justify-center bg-[#00E676] text-black text-xs font-bold rounded">V</span>;
      case 'D':
        return <span className="w-6 h-6 flex items-center justify-center bg-[#FFEA00] text-black text-xs font-bold rounded">P</span>;
      case 'L':
        return <span className="w-6 h-6 flex items-center justify-center bg-[#FF1744] text-white text-xs font-bold rounded">S</span>;
      default:
        return null;
    }
  };

  const getConfidenceBadge = (confidence) => {
    switch (confidence) {
      case 'Alta':
        return <span className="px-2 py-0.5 bg-[#00E676]/20 text-[#00E676] text-xs rounded border border-[#00E676]/30">Alta</span>;
      case 'Media':
        return <span className="px-2 py-0.5 bg-[#FFEA00]/20 text-[#FFEA00] text-xs rounded border border-[#FFEA00]/30">Media</span>;
      default:
        return <span className="px-2 py-0.5 bg-[#FF1744]/20 text-[#FF1744] text-xs rounded border border-[#FF1744]/30">Bassa</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Unlock screen
  if (!statsStatus?.stats_unlocked) {
    return (
      <div className="space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3"
        >
          <BarChart3 size={28} className="text-[#FFEA00]" />
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
              {isItalian ? 'Statistiche & Pronostici' : 'Stats & Predictions'}
            </h1>
            <p className="text-slate-400 text-sm">{isItalian ? 'Analisi avanzate e previsioni' : 'Advanced analysis and predictions'}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-xl mx-auto"
        >
          <div className="bg-[#0a0d15] border border-white/10  p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#FFEA00]/10 border border-[#FFEA00]/30 flex items-center justify-center">
              <Lock size={40} className="text-[#FFEA00]" />
            </div>

            <h2 className="font-heading text-2xl font-bold text-white uppercase tracking-wider mb-4">
              {isItalian ? 'Statistiche Bloccate' : 'Stats Locked'}
            </h2>

            <p className="text-slate-400 mb-6">
              {isItalian
                ? 'Sblocca le statistiche avanzate per accedere a classifica marcatori, assist, pronostici delle partite e molto altro!'
                : 'Unlock advanced statistics to access top scorers, assists, match predictions and more!'}
            </p>

            {/* Features preview */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="p-3 bg-[#050508] border border-white/10 ">
                <Target size={20} className="mx-auto mb-2 text-[#00E676]" />
                <p className="text-xs text-slate-300">{isItalian ? 'Top Marcatori' : 'Top Scorers'}</p>
              </div>
              <div className="p-3 bg-[#050508] border border-white/10 ">
                <Users size={20} className="mx-auto mb-2 text-[#00F0FF]" />
                <p className="text-xs text-slate-300">{isItalian ? 'Top Assist' : 'Top Assists'}</p>
              </div>
              <div className="p-3 bg-[#050508] border border-white/10 ">
                <TrendingUp size={20} className="mx-auto mb-2 text-[#FF9100]" />
                <p className="text-xs text-slate-300">{isItalian ? 'Pronostici' : 'Predictions'}</p>
              </div>
              <div className="p-3 bg-[#050508] border border-white/10 ">
                <Trophy size={20} className="mx-auto mb-2 text-[#FFEA00]" />
                <p className="text-xs text-slate-300">{isItalian ? 'Classifica Club' : 'Club Rankings'}</p>
              </div>
            </div>

            <div className="bg-[#050508] border border-white/10  p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-slate-400">{isItalian ? 'Costo Sblocco' : 'Unlock Cost'}</span>
                <div className="flex items-center gap-2 text-[#FFEA00]">
                  <Coins size={18} />
                  <span className="font-mono font-bold">{statsStatus?.unlock_cost?.toLocaleString()}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">{isItalian ? 'I Tuoi Coins' : 'Your Coins'}</span>
                <div className="flex items-center gap-2 text-white">
                  <Coins size={18} />
                  <span className="font-mono font-bold">{statsStatus?.personal_coins?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {statsStatus?.personal_coins >= statsStatus?.unlock_cost ? (
              <Button
                onClick={handleUnlock}
                disabled={unlocking}
                className="w-full bg-gradient-to-r from-[#FFEA00] to-[#FF9100] hover:opacity-90 text-black font-bold uppercase tracking-wider"
                data-testid="unlock-stats-btn"
              >
                {unlocking ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Unlock size={18} className="mr-2" />
                    {isItalian ? 'Sblocca Statistiche' : 'Unlock Stats'}
                  </>
                )}
              </Button>
            ) : (
              <div className="space-y-3">
                <Button disabled className="w-full bg-slate-700 text-slate-400 cursor-not-allowed">
                  <Lock size={18} className="mr-2" />
                  {isItalian ? 'Coins Insufficienti' : 'Not Enough Coins'}
                </Button>
                <p className="text-sm text-[#FF1744]">
                  {isItalian
                    ? `Ti mancano ${(statsStatus?.unlock_cost - statsStatus?.personal_coins).toLocaleString()} coins`
                    : `You need ${(statsStatus?.unlock_cost - statsStatus?.personal_coins).toLocaleString()} more coins`}
                </p>
                <Button
                  variant="outline"
                  className="w-full border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10"
                  onClick={() => (window.location.href = '/shop')}
                  data-testid="go-to-shop-btn"
                >
                  <Coins size={18} className="mr-2" />
                  {isItalian ? 'Vai allo Shop' : 'Go to Shop'}
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // Stats content
  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <BarChart3 size={28} className="text-[#FFEA00]" />
          <div className="min-w-0">
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
              {isItalian ? 'Statistiche & Pronostici' : 'Stats & Predictions'}
            </h1>
            <p className="text-slate-400 text-sm">
              {advancedStats?.total_matches_analyzed || 0} {isItalian ? 'partite analizzate' : 'matches analyzed'}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <Tabs defaultValue="predictions" className="w-full">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="bg-[#0a0d15] border border-white/10 p-1 inline-flex min-w-max md:grid md:grid-cols-4 md:w-full">
            <TabsTrigger value="predictions" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white whitespace-nowrap px-3 md:px-2">
              <TrendingUp size={14} className="mr-1.5" />
              <span className="text-xs md:text-sm">{isItalian ? 'Pronostici' : 'Predictions'}</span>
            </TabsTrigger>
            <TabsTrigger value="scorers" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white whitespace-nowrap px-3 md:px-2">
              <Target size={14} className="mr-1.5" />
              <span className="text-xs md:text-sm">{isItalian ? 'Marcatori' : 'Scorers'}</span>
            </TabsTrigger>
            <TabsTrigger value="assists" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white whitespace-nowrap px-3 md:px-2">
              <Users size={14} className="mr-1.5" />
              <span className="text-xs md:text-sm">Assist</span>
            </TabsTrigger>
            <TabsTrigger value="clubs" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white whitespace-nowrap px-3 md:px-2">
              <Shield size={14} className="mr-1.5" />
              <span className="text-xs md:text-sm">Club</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Predictions Tab */}
        <TabsContent value="predictions" className="mt-6">
          <div className="space-y-4">
            {advancedStats?.predictions?.length > 0 ? (
              advancedStats.predictions.map((pred, index) => (
                <motion.div
                  key={pred.match_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-[#0a0d15] border border-white/10  p-4"
                  data-testid={`prediction-${pred.match_id}`}
                >
                  {/* Match header */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-slate-500">
                      {pred.match_date ? new Date(pred.match_date).toLocaleDateString() : isItalian ? 'Da definire' : 'TBD'}
                    </span>
                    <div className="flex items-center gap-2">
                      <Zap size={14} className="text-[#FFEA00]" />
                      {getConfidenceBadge(pred.confidence)}
                    </div>
                  </div>

                  {/* Teams */}
                  <div className="grid grid-cols-3 gap-4 items-center mb-4">
                    {/* Home team */}
                    <div className="text-center">
                      <div
                        className="w-12 h-12 mx-auto mb-2 rounded border border-white/10 overflow-hidden"
                        style={{ backgroundColor: pred.home_club?.colors?.primary || '#2979FF' }}
                      >
                        {pred.home_club?.logo ? (
                          <img src={pred.home_club.logo} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Shield size={24} className="text-white/80" />
                          </div>
                        )}
                      </div>
                      <p className="font-medium text-white text-sm">{pred.home_club?.name}</p>
                    </div>

                    {/* VS / Predicted Score */}
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">{isItalian ? 'Pronostico' : 'Prediction'}</p>
                      <p className="font-mono text-2xl font-bold text-[#FFEA00]">{pred.predicted_score}</p>
                    </div>

                    {/* Away team */}
                    <div className="text-center">
                      <div
                        className="w-12 h-12 mx-auto mb-2 rounded border border-white/10 overflow-hidden"
                        style={{ backgroundColor: pred.away_club?.colors?.primary || '#FF1744' }}
                      >
                        {pred.away_club?.logo ? (
                          <img src={pred.away_club.logo} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Shield size={24} className="text-white/80" />
                          </div>
                        )}
                      </div>
                      <p className="font-medium text-white text-sm">{pred.away_club?.name}</p>
                    </div>
                  </div>

                  {/* Probability bars */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 w-8">1</span>
                      <div className="flex-1 h-6 bg-[#050508]  overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#00E676] to-[#00C853] flex items-center justify-end pr-2"
                          style={{ width: `${pred.home_win_prob}%` }}
                        >
                          <span className="text-xs font-bold text-black">{pred.home_win_prob}%</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 w-8">X</span>
                      <div className="flex-1 h-6 bg-[#050508]  overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#FFEA00] to-[#FF9100] flex items-center justify-end pr-2"
                          style={{ width: `${pred.draw_prob}%` }}
                        >
                          <span className="text-xs font-bold text-black">{pred.draw_prob}%</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 w-8">2</span>
                      <div className="flex-1 h-6 bg-[#050508]  overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#2979FF] to-[#00B0FF] flex items-center justify-end pr-2"
                          style={{ width: `${pred.away_win_prob}%` }}
                        >
                          <span className="text-xs font-bold text-black">{pred.away_win_prob}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12 bg-[#0a0d15] border border-white/10 ">
                <TrendingUp size={48} className="mx-auto text-slate-600 mb-4" />
                <p className="text-slate-500">
                  {isItalian ? 'Nessuna partita in programma per i pronostici' : 'No upcoming matches for predictions'}
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Top Scorers Tab */}
        <TabsContent value="scorers" className="mt-6">
          <div className="bg-[#0a0d15] border border-white/10  overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center gap-2">
              <Target size={20} className="text-[#00E676]" />
              <h3 className="font-heading text-lg font-bold uppercase tracking-wider text-white">
                {isItalian ? 'Classifica Marcatori' : 'Top Scorers'}
              </h3>
            </div>
            
            {advancedStats?.top_scorers?.length > 0 ? (
              <div className="divide-y divide-white/5">
                {advancedStats.top_scorers.map((scorer, index) => (
                  <motion.div
                    key={scorer.player_id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 flex items-center gap-4 hover:bg-white/5"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-[#FFEA00] text-black' :
                      index === 1 ? 'bg-slate-400 text-black' :
                      index === 2 ? 'bg-[#CD7F32] text-black' :
                      'bg-[#050508] text-slate-400 border border-white/10'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-white">{scorer.gamertag}</p>
                      <p className="text-xs text-slate-500">{scorer.club_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xl font-bold text-[#00E676]">{scorer.goals}</p>
                      <p className="text-xs text-slate-500">{scorer.matches_played} {isItalian ? 'partite' : 'matches'}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                {isItalian ? 'Nessun marcatore registrato' : 'No scorers recorded'}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Top Assists Tab */}
        <TabsContent value="assists" className="mt-6">
          <div className="bg-[#0a0d15] border border-white/10  overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center gap-2">
              <Users size={20} className="text-[#00F0FF]" />
              <h3 className="font-heading text-lg font-bold uppercase tracking-wider text-white">
                {isItalian ? 'Classifica Assist' : 'Top Assists'}
              </h3>
            </div>
            
            {advancedStats?.top_assists?.length > 0 ? (
              <div className="divide-y divide-white/5">
                {advancedStats.top_assists.map((player, index) => (
                  <motion.div
                    key={player.player_id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 flex items-center gap-4 hover:bg-white/5"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-[#FFEA00] text-black' :
                      index === 1 ? 'bg-slate-400 text-black' :
                      index === 2 ? 'bg-[#CD7F32] text-black' :
                      'bg-[#050508] text-slate-400 border border-white/10'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-white">{player.gamertag}</p>
                      <p className="text-xs text-slate-500">{player.club_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xl font-bold text-[#00F0FF]">{player.assists}</p>
                      <p className="text-xs text-slate-500">{player.matches_played} {isItalian ? 'partite' : 'matches'}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                {isItalian ? 'Nessun assist registrato' : 'No assists recorded'}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Club Rankings Tab */}
        <TabsContent value="clubs" className="mt-6">
          <div className="bg-[#0a0d15] border border-white/10  overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center gap-2">
              <Trophy size={20} className="text-[#FFEA00]" />
              <h3 className="font-heading text-lg font-bold uppercase tracking-wider text-white">
                {isItalian ? 'Statistiche Club' : 'Club Statistics'}
              </h3>
            </div>
            
            {advancedStats?.club_rankings?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 text-xs text-slate-500 uppercase">
                      <th className="p-3 text-left">#</th>
                      <th className="p-3 text-left">Club</th>
                      <th className="p-3 text-center">G</th>
                      <th className="p-3 text-center">V</th>
                      <th className="p-3 text-center">P</th>
                      <th className="p-3 text-center">S</th>
                      <th className="p-3 text-center">GF</th>
                      <th className="p-3 text-center">GS</th>
                      <th className="p-3 text-center">DR</th>
                      <th className="p-3 text-center">Pts</th>
                      <th className="p-3 text-center">{isItalian ? 'Forma' : 'Form'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {advancedStats.club_rankings.map((club, index) => (
                      <motion.tr
                        key={club.club_id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.03 }}
                        className="border-b border-white/5 hover:bg-white/5"
                      >
                        <td className="p-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            index === 0 ? 'bg-[#FFEA00] text-black' :
                            index === 1 ? 'bg-slate-400 text-black' :
                            index === 2 ? 'bg-[#CD7F32] text-black' :
                            'text-slate-400'
                          }`}>
                            {index + 1}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setRosterClub(club)} data-testid={`standings-club-${club.club_id}`}>
                            <div
                              className="w-8 h-8 rounded overflow-hidden"
                              style={{ backgroundColor: club.club_colors?.primary || '#2979FF' }}
                            >
                              {club.club_logo ? (
                                <img src={club.club_logo} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Shield size={16} className="text-white/80" />
                                </div>
                              )}
                            </div>
                            <span className="font-medium text-white">{club.club_name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center text-slate-300">{club.played}</td>
                        <td className="p-3 text-center text-[#00E676]">{club.won}</td>
                        <td className="p-3 text-center text-[#FFEA00]">{club.drawn}</td>
                        <td className="p-3 text-center text-[#FF1744]">{club.lost}</td>
                        <td className="p-3 text-center text-slate-300">{club.goals_for}</td>
                        <td className="p-3 text-center text-slate-300">{club.goals_against}</td>
                        <td className="p-3 text-center">
                          <span className={club.goal_diff >= 0 ? 'text-[#00E676]' : 'text-[#FF1744]'}>
                            {club.goal_diff >= 0 ? '+' : ''}{club.goal_diff}
                          </span>
                        </td>
                        <td className="p-3 text-center font-bold text-white">{club.points}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1 justify-center">
                            {club.form?.map((result, i) => (
                              <span key={i}>{getFormBadge(result)}</span>
                            ))}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                {isItalian ? 'Nessuna statistica club disponibile' : 'No club statistics available'}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
      {rosterClub && (
        <ClubRosterDialog
          clubId={rosterClub.club_id}
          clubName={rosterClub.club_name}
          clubLogo={rosterClub.club_logo}
          onClose={() => setRosterClub(null)}
        />
      )}
    </div>
  );
};

export default Stats;
